import { z } from "zod";

export const FEEDBACK_TYPES = [
  "bug",
  "feature_request",
  "general",
  "ui_ux",
  "other",
] as const;

export type FeedbackType = (typeof FEEDBACK_TYPES)[number];

const screenshotSchema = z
  .string()
  .trim()
  .max(2048, "Screenshot URL is too long")
  .refine(
    (value) => {
      if (!value) {
        return true;
      }
      try {
        const url = new URL(value);
        if (url.protocol === "https:") {
          return true;
        }
        const isLocalhost =
          url.protocol === "http:" &&
          (url.hostname === "localhost" || url.hostname === "127.0.0.1");
        return isLocalhost;
      } catch {
        return false;
      }
    },
    { message: "Screenshot must be a valid https URL (or localhost in development)" }
  )
  .optional()
  .or(z.literal(""));

export const feedbackClientSchema = z.object({
  title: z.string().trim().min(3).max(140),
  name: z.string().trim().max(120).optional().or(z.literal("")),
  email: z
    .string()
    .trim()
    .email("Invalid email address")
    .max(320)
    .optional()
    .or(z.literal("")),
  type: z.enum(FEEDBACK_TYPES),
  rating: z.number().int().min(1).max(5),
  message: z.string().trim().min(10).max(4000),
  screenshotUrl: screenshotSchema,
  screenResolution: z.string().trim().max(40).optional().or(z.literal("")),
  language: z.string().trim().max(40).optional().or(z.literal("")),
  timezone: z.string().trim().max(80).optional().or(z.literal("")),
  identifiedUserId: z.string().trim().max(120).optional().or(z.literal("")),
  pageUrl: z.string().trim().max(2048).optional().or(z.literal("")),
});

export const feedbackServerSchema = feedbackClientSchema.extend({
  status: z.enum(["new", "reviewed", "closed"]).default("new"),
  ipAddress: z.string().max(200),
  userAgent: z.string().max(1024),
  browser: z.string().max(120),
  os: z.string().max(120),
  deviceType: z.enum(["desktop", "mobile", "tablet", "bot", "unknown"]),
  createdAt: z.coerce.date(),
});

export type FeedbackClientInput = z.infer<typeof feedbackClientSchema>;
export type FeedbackServerInput = z.infer<typeof feedbackServerSchema>;

export type FeedbackWidgetPosition =
  | "bottom-right"
  | "bottom-left"
  | "top-right"
  | "top-left";

export type FeedbackWidgetButtonStyle = "icon-text" | "icon-only";

export type FeedbackWidgetProps = {
  brandColor?: string;
  position?: FeedbackWidgetPosition;
  logoUrl?: string;
  hideBranding?: boolean;
  title?: string;
  buttonText?: string;
  buttonStyle?: FeedbackWidgetButtonStyle;
  successMessage?: string;
  apiEndpoint?: string;
  roadmapUrl?: string;
  workspaceKey?: string;
  submissionsEnabled?: boolean;
  allowGuestSubmissions?: boolean;
  showRoadmapLink?: boolean;
  user?: {
    id?: string;
    name?: string;
    email?: string;
  };
  openSignal?: number;
};
